/**
 * App Version editor — edits `system/appVersion`.
 *
 * Decides the in-app update prompt behaviour on every running mobile
 * client:
 *
 *   installed >= latest                          → no prompt
 *   minimum <= installed < latest                → soft (dismissible)
 *   installed < minimum                          → force (blocking)
 *
 * Two preset buttons cover the common cases:
 *   • Soft-update only          — sets latest = typed, minimum unchanged
 *   • Force-update everyone     — sets latest = typed, minimum = latest
 *
 * Manual edit lets you set intermediate min/latest for the "force users
 * on very old builds only" scenario.
 */
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconBrandApple,
  IconBrandGooglePlay,
  IconDeviceFloppy,
  IconLockOpen,
  IconLock,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';

import { callAdmin, db } from '@/lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

type Form = {
  latestIos: string;
  latestAndroid: string;
  minimumIos: string;
  minimumAndroid: string;
  releaseNotes: string;
  updateUrlIos: string;
  updateUrlAndroid: string;
};

const EMPTY: Form = {
  latestIos: '1.0.0',
  latestAndroid: '1.0.0',
  minimumIos: '1.0.0',
  minimumAndroid: '1.0.0',
  releaseNotes: '',
  updateUrlIos: 'https://apps.apple.com/in/app/interior-os/id6766840867',
  updateUrlAndroid:
    'https://play.google.com/store/apps/details?id=com.siteexpens.app',
};

export function AppVersionRoute() {
  const [form, setForm] = useState<Form>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'system', 'appVersion'), (snap) => {
      if (snap.exists()) {
        const d = snap.data() as Partial<Form>;
        setForm({
          latestIos: d.latestIos ?? EMPTY.latestIos,
          latestAndroid: d.latestAndroid ?? EMPTY.latestAndroid,
          minimumIos: d.minimumIos ?? EMPTY.minimumIos,
          minimumAndroid: d.minimumAndroid ?? EMPTY.minimumAndroid,
          releaseNotes: d.releaseNotes ?? '',
          updateUrlIos: d.updateUrlIos ?? EMPTY.updateUrlIos,
          updateUrlAndroid: d.updateUrlAndroid ?? EMPTY.updateUrlAndroid,
        });
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const isForceIos = form.minimumIos === form.latestIos;
  const isForceAndroid = form.minimumAndroid === form.latestAndroid;
  const anyForce = isForceIos || isForceAndroid;

  const save = async (next: Form) => {
    setSaving(true);
    try {
      await callAdmin.updateAppVersion(next);
      notifications.show({
        color: 'green',
        title: 'Saved',
        message: 'App version config updated. Live on devices within seconds.',
      });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Save failed',
        message: (e as Error).message ?? 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  const applySoftOnly = () => {
    // Keep typed `latest` values; reset minimums to what they were in
    // Firestore (i.e. don't change minimum) by only sending the
    // current minimum-from-form which the user hasn't touched.
    void save(form);
  };

  const applyForce = () => {
    if (
      !window.confirm(
        `This will BLOCK every user running below v${form.latestIos} (iOS) ` +
          `and v${form.latestAndroid} (Android) from using the app until ` +
          `they update. Continue?`,
      )
    )
      return;
    void save({
      ...form,
      minimumIos: form.latestIos,
      minimumAndroid: form.latestAndroid,
    });
  };

  const previewStatus = useMemo(() => {
    const lines: string[] = [];
    if (isForceIos)
      lines.push(`iOS: FORCE-update everyone below ${form.latestIos}`);
    else if (form.minimumIos !== form.latestIos)
      lines.push(
        `iOS: soft prompt v${form.minimumIos}..${form.latestIos}, FORCE below ${form.minimumIos}`,
      );
    if (isForceAndroid)
      lines.push(`Android: FORCE-update everyone below ${form.latestAndroid}`);
    else if (form.minimumAndroid !== form.latestAndroid)
      lines.push(
        `Android: soft prompt v${form.minimumAndroid}..${form.latestAndroid}, FORCE below ${form.minimumAndroid}`,
      );
    return lines;
  }, [form, isForceIos, isForceAndroid]);

  return (
    <Stack gap="lg">
      <div>
        <Title order={2}>App Version</Title>
        <Text c="dimmed" size="sm">
          Controls the in-app update prompt. Bump <b>Latest</b> only for a
          dismissible banner; bump <b>Minimum</b> to the latest version to
          force-update everyone on old builds.
        </Text>
      </div>

      {anyForce ? (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          <Text fw={600}>Force update is ACTIVE</Text>
          <Text size="sm">
            Anyone on a build below the listed minimum can't use the app until
            they update. Use this only for critical fixes (broken server
            contract, security issue, etc.).
          </Text>
        </Alert>
      ) : null}

      {loading ? (
        <Text c="dimmed">Loading…</Text>
      ) : (
        <Group align="flex-start" grow>
          {/* iOS */}
          <Card withBorder radius="md" padding="lg">
            <Group justify="space-between" mb="xs">
              <Group gap={6}>
                <IconBrandApple size={18} />
                <Title order={4}>iOS</Title>
              </Group>
              <Badge color={isForceIos ? 'red' : 'gray'} variant="light">
                {isForceIos ? (
                  <Group gap={4}>
                    <IconLock size={11} /> Force
                  </Group>
                ) : (
                  <Group gap={4}>
                    <IconLockOpen size={11} /> Soft
                  </Group>
                )}
              </Badge>
            </Group>
            <Stack gap="sm">
              <TextInput
                label="Latest"
                placeholder="1.0.6"
                value={form.latestIos}
                onChange={(e) =>
                  setForm({ ...form, latestIos: e.currentTarget.value })
                }
                description="Newest version live on the App Store."
              />
              <TextInput
                label="Minimum supported"
                placeholder="1.0.0"
                value={form.minimumIos}
                onChange={(e) =>
                  setForm({ ...form, minimumIos: e.currentTarget.value })
                }
                description="Anything below this triggers the force-update modal."
              />
              <TextInput
                label="Store URL"
                placeholder="https://apps.apple.com/in/app/…"
                value={form.updateUrlIos}
                onChange={(e) =>
                  setForm({ ...form, updateUrlIos: e.currentTarget.value })
                }
              />
            </Stack>
          </Card>

          {/* Android */}
          <Card withBorder radius="md" padding="lg">
            <Group justify="space-between" mb="xs">
              <Group gap={6}>
                <IconBrandGooglePlay size={18} />
                <Title order={4}>Android</Title>
              </Group>
              <Badge color={isForceAndroid ? 'red' : 'gray'} variant="light">
                {isForceAndroid ? (
                  <Group gap={4}>
                    <IconLock size={11} /> Force
                  </Group>
                ) : (
                  <Group gap={4}>
                    <IconLockOpen size={11} /> Soft
                  </Group>
                )}
              </Badge>
            </Group>
            <Stack gap="sm">
              <TextInput
                label="Latest"
                placeholder="1.0.6"
                value={form.latestAndroid}
                onChange={(e) =>
                  setForm({ ...form, latestAndroid: e.currentTarget.value })
                }
                description="Newest version live on the Play Store."
              />
              <TextInput
                label="Minimum supported"
                placeholder="1.0.0"
                value={form.minimumAndroid}
                onChange={(e) =>
                  setForm({ ...form, minimumAndroid: e.currentTarget.value })
                }
                description="Anything below this triggers the force-update modal."
              />
              <TextInput
                label="Store URL"
                placeholder="https://play.google.com/store/…"
                value={form.updateUrlAndroid}
                onChange={(e) =>
                  setForm({ ...form, updateUrlAndroid: e.currentTarget.value })
                }
              />
            </Stack>
          </Card>
        </Group>
      )}

      <Card withBorder radius="md" padding="lg">
        <Textarea
          label="Release notes"
          description="Shown to users inside the update prompt. Keep it punchy — 2-3 bullets max."
          placeholder={'• Designer role\n• Multi-receipt support\n• CRM filter improvements'}
          autosize
          minRows={3}
          value={form.releaseNotes}
          onChange={(e) =>
            setForm({ ...form, releaseNotes: e.currentTarget.value })
          }
        />
      </Card>

      {previewStatus.length > 0 ? (
        <Card withBorder radius="md" padding="md" bg="gray.0">
          <Text fw={600} size="sm" mb={4}>
            Preview
          </Text>
          {previewStatus.map((line, i) => (
            <Text key={i} size="sm" c="dimmed">
              • {line}
            </Text>
          ))}
        </Card>
      ) : null}

      <Group justify="flex-end">
        <Button
          color="red"
          variant="outline"
          leftSection={<IconLock size={14} />}
          loading={saving}
          onClick={applyForce}
        >
          Force-update everyone to latest
        </Button>
        <Button
          color="blue"
          leftSection={<IconDeviceFloppy size={14} />}
          loading={saving}
          onClick={applySoftOnly}
        >
          Save
        </Button>
      </Group>
    </Stack>
  );
}
